import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// Components
import SearchIcon from 'assets/images/svg/SearchIcon';
// Helpers
import { EXPERIENCE_LEVELS } from 'constants/OrdersFilters';

const VISIBLE_ORDERS = 2;
const SEARCH_DEBOUNCE = 400;

/**
 * HOF dashboard - the florists of the OPS scope with their status, load and the orders
 * they are holding. Only the first couple of orders are listed, the rest open on demand.
 */
const HofDashboardTable = ({ rows, isLoading, search = '', onSearchChange }) => {
  const navigate = useNavigate();
  const [expandedFlorist, setExpandedFlorist] = useState(null);
  const [searchText, setSearchText] = useState(search);
  // Sorting is local: the endpoint returns the whole list in one go.
  const [sort, setSort] = useState({ key: null, dir: 'desc' });

  const sortedRows = useMemo(() => {
    if (!sort.key || !Array.isArray(rows)) {
      return rows ?? [];
    }
    const valueOf = (florist) =>
      sort.key === 'status' ? Number(Boolean(florist?.isOnline)) : florist?.activeOrders ?? 0;
    return [...rows].sort((a, b) =>
      sort.dir === 'asc' ? valueOf(a) - valueOf(b) : valueOf(b) - valueOf(a)
    );
  }, [rows, sort]);

  // First click puts the busiest / online florists on top, the next flips it.
  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
    );

  const sortArrow = (key) => {
    if (sort.key !== key) {
      return '↕';
    }
    return sort.dir === 'desc' ? '↓' : '↑';
  };

  // Hold off on the request until typing settles.
  useEffect(() => {
    if (searchText === search) {
      return undefined;
    }
    const timer = setTimeout(() => onSearchChange(searchText.trim()), SEARCH_DEBOUNCE);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  return (
    <>
      <div className="hof-toolbar">
        <form className="hof-search" onSubmit={(e) => e.preventDefault()}>
          <SearchIcon />
          <input
            id="hof-search-input"
            placeholder="Search by florist name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button type="button" className="hof-search-clear" onClick={() => setSearchText('')}>
              &times;
            </button>
          )}
        </form>
      </div>

      <div className="hof-dashboard-table">
      <div className="hof-row hof-head">
        <span>Florist</span>
        <span>Skill level</span>
        <span>
          <button
            type="button"
            className={`hof-sort${sort.key === 'status' ? ' active' : ''}`}
            onClick={() => toggleSort('status')}
          >
            Status <i>{sortArrow('status')}</i>
          </button>
        </span>
        <span>
          <button
            type="button"
            className={`hof-sort${sort.key === 'assigned' ? ' active' : ''}`}
            onClick={() => toggleSort('assigned')}
          >
            Assigned <i>{sortArrow('assigned')}</i>
          </button>
        </span>
        <span>Order IDs — click an order to open its details</span>
      </div>

      {sortedRows?.length > 0 ? (
        sortedRows.map((florist) => {
          const orderIds = florist?.assignedOrderIds ?? [];
          const isExpanded = expandedFlorist === florist?.userId;
          const visibleOrders = isExpanded ? orderIds : orderIds.slice(0, VISIBLE_ORDERS);
          const hiddenCount = orderIds.length - visibleOrders.length;

          return (
            <div className="hof-row" key={florist?.userId}>
              <span className="hof-florist">{florist?.name}</span>

              <span>
                <span className="hof-skill">
                  {EXPERIENCE_LEVELS[florist?.experienceLevel] ?? '-'}
                </span>
              </span>

              <span className={`hof-status${florist?.isOnline ? ' online' : ''}`}>
                <span className="hof-status-dot" />
                {florist?.isOnline ? 'Online' : 'Offline'}
              </span>

              <span className="hof-assigned">
                <b>{florist?.activeOrders ?? 0}</b>
                <small>/ {florist?.maxActiveOrders ?? 0}</small>
              </span>

              <span className="hof-orders">
                {visibleOrders.map((orderId) => (
                  <button
                    type="button"
                    key={orderId}
                    className="hof-order-chip"
                    onClick={() => navigate(`/orderdetails/${orderId}`)}
                  >
                    {orderId}
                  </button>
                ))}

                {hiddenCount > 0 && (
                  <button
                    type="button"
                    className="hof-order-chip more"
                    onClick={() => setExpandedFlorist(florist?.userId)}
                  >
                    +{hiddenCount} more
                  </button>
                )}

                {isExpanded && orderIds.length > VISIBLE_ORDERS && (
                  <button
                    type="button"
                    className="hof-order-chip more"
                    onClick={() => setExpandedFlorist(null)}
                  >
                    Show less
                  </button>
                )}

                {orderIds.length === 0 && <span className="hof-no-orders">-</span>}
              </span>
            </div>
          );
        })
        ) : (
          <h4 className="no-orders-title">{isLoading ? '' : 'No florists'}</h4>
        )}
      </div>
    </>
  );
};

export default HofDashboardTable;
